import { CSSProperties, useRef } from "react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";

/**
 * SYSTEM_DESIGN.md §6.2 / INC-03 — Columna de acciones en tablas.
 *
 *   ≤ 3 acciones → íconos circulares outline en línea
 *   > 3 acciones → dropdown "Acciones"
 *
 * La regla la decide el componente a partir de `actions.length`. Por eso no se
 * puede violar agregando una cuarta acción sin darse cuenta: el render cambia
 * solo. Quedan erradicados los links de texto ("Rol", "Revocar") y los botones
 * planos de color ("Banear") — no hay forma de expresarlos con esta API.
 */
export const ROW_ACTIONS_ICON_LIMIT = 3;

export interface RowAction {
  icon: string;
  /** Texto accesible; en modo dropdown es la etiqueta visible del ítem. */
  label: string;
  onClick: () => void;
  severity?: "info" | "success" | "danger" | "warning";
  disabled?: boolean;
  /** Motivo del bloqueo: reemplaza al tooltip cuando la acción está deshabilitada. */
  disabledReason?: string;
  visible?: boolean;
}

/**
 * `collapsed`: las acciones quedan detrás de un botón de tres puntos que abre un menú con ícono y texto. Lo usa
 * Reglas de Puntos, donde la tabla tiene demasiadas columnas para los tres íconos a la vista.
 */
export const RowActions = ({
  actions,
  collapsed = false,
}: {
  actions: RowAction[];
  collapsed?: boolean;
}) => {
  const visible = actions.filter((a) => a.visible !== false);
  if (visible.length === 0) return null;

  if (visible.length > ROW_ACTIONS_ICON_LIMIT) {
    return <RowActionsDropdown actions={visible} />;
  }

  return collapsed ? <RowActionsCollapsed actions={visible} /> : <RowActionIcons actions={visible} />;
};

const RowActionIcons = ({ actions }: { actions: RowAction[] }) => (
  <div className="row-actions flex gap-2">
    {actions.map((a) => (
      <Button
        key={a.label}
        icon={a.icon}
        rounded
        outlined
        size="small"
        severity={a.severity}
        disabled={a.disabled}
        onClick={a.onClick}
        aria-label={a.label}
        tooltip={a.disabled ? a.disabledReason ?? a.label : a.label}
        tooltipOptions={{ position: "top", showOnDisabled: Boolean(a.disabledReason) }}
      />
    ))}
  </div>
);

const RowActionsCollapsed = ({ actions }: { actions: RowAction[] }) => {
  const menuRef = useRef<Menu>(null);

  return (
    <div className="row-actions">
      <Button
        type="button"
        icon="pi pi-ellipsis-h"
        rounded
        size="small"
        className="row-actions-more"
        aria-label="Acciones"
        aria-haspopup
        onClick={(e) => menuRef.current?.toggle(e)}
      />
      <Menu
        ref={menuRef}
        model={toRowActionsMenuModel(actions)}
        popup
        popupAlignment="right"
        className="row-actions-more-menu"
      />
    </div>
  );
};

const toRowActionsMenuModel = (actions: RowAction[]): MenuItem[] =>
  actions.map((a) => ({
    label: a.label,
    icon: a.icon,
    disabled: a.disabled,
    command: a.onClick,
    className: a.severity === "danger" ? "row-actions-menu__item--danger" : undefined,
  }));

/**
 * > 3 acciones: un único botón "Acciones" con chevron que abre el menú con
 * TODAS las acciones. Antes era un SplitButton: la parte "Acciones" ejecutaba
 * en silencio la primera acción (ej. "Ver") y solo el chevron abría el menú,
 * así que el botón no hacía lo que decía.
 */
const RowActionsDropdown = ({ actions }: { actions: RowAction[] }) => {
  const menuRef = useRef<Menu>(null);
  const model: MenuItem[] = toRowActionsMenuModel(actions);

  return (
    <>
      <Button
        type="button"
        label="Acciones"
        icon="pi pi-chevron-down"
        iconPos="right"
        size="small"
        outlined
        aria-haspopup
        onClick={(e) => menuRef.current?.toggle(e)}
      />
      <Menu ref={menuRef} model={model} popup popupAlignment="right" />
    </>
  );
};

/**
 * Margen simétrico de las tablas con columna "Acciones": `edge` es la distancia
 * del borde izquierdo de la tabla al texto de la primera columna de datos
 * (manija de arrastre + padding), y se usa como margen derecho del bloque de
 * acciones. `width` es el ancho del bloque (título e íconos centrados en él).
 */
export const rowActionsTableStyle = (edge: string, width: string): CSSProperties =>
  ({ "--table-edge": edge, "--row-actions-w": width }) as CSSProperties;
